import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import api from '../services/api';
import './StudentSearchSelect.css';

const DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 50;
const BROWSE_LIMIT = 30;

const StudentSearchSelect = ({
  value,
  onChange,
  disabled = false,
  hasError = false,
  placeholder = 'Buscar por nombre o DNI...',
  inputClassName = '',
  hiddenInputProps = null,
  showAddNew = false,
  onAddNew,
}) => {
  const [inputText, setInputText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  const normalizedValue = value ? String(value) : '';

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputText.trim());
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputText]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data: selectedStudent } = useQuery(
    ['student', normalizedValue],
    async () => {
      const response = await api.get(`/students/${normalizedValue}`);
      return response.data;
    },
    { enabled: !!normalizedValue }
  );

  useEffect(() => {
    if (selectedStudent && !showDropdown) {
      setInputText(`${selectedStudent.first_name} ${selectedStudent.last_name}`);
    }
  }, [selectedStudent, showDropdown]);

  const { data: students = [], isLoading, isFetching } = useQuery(
    ['students-search', debouncedSearch, showDropdown],
    async () => {
      const params = {
        is_active: true,
        limit: debouncedSearch ? SEARCH_LIMIT : BROWSE_LIMIT,
      };
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      const response = await api.get('/students', { params });
      return response.data;
    },
    {
      enabled: showDropdown && !disabled,
      keepPreviousData: true,
      staleTime: 5000,
    }
  );

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    setInputText(nextValue);
    setShowDropdown(true);
    if (!nextValue) {
      onChange('', null);
    }
  };

  const handleClear = () => {
    setInputText('');
    setDebouncedSearch('');
    onChange('', null);
    setShowDropdown(false);
  };

  const handleSelect = (student) => {
    onChange(student.id, student);
    setInputText(`${student.first_name} ${student.last_name}`);
    setShowDropdown(false);
  };

  const showClearButton = !!inputText && !disabled;
  const isSearching = isLoading || isFetching;

  return (
    <div className="student-search-wrapper" ref={wrapperRef}>
      <input
        type="text"
        placeholder={placeholder}
        value={inputText}
        onChange={handleInputChange}
        onFocus={() => setShowDropdown(true)}
        autoComplete="off"
        disabled={disabled}
        className={`${inputClassName} ${hasError ? 'input-error' : ''}`.trim()}
      />
      {showClearButton && (
        <button
          type="button"
          className="student-search-clear"
          onClick={handleClear}
          title="Limpiar"
        >
          ×
        </button>
      )}
      {showDropdown && !disabled && (
        <div className="student-search-dropdown">
          {showAddNew && (
            <div
              className="student-search-add-new"
              onMouseDown={(event) => {
                event.preventDefault();
                setShowDropdown(false);
                onAddNew?.();
              }}
            >
              ➕ Registrar nuevo alumno
            </div>
          )}
          {!debouncedSearch && (
            <div className="student-search-hint">
              Escribe nombre o DNI para buscar entre todos los alumnos
            </div>
          )}
          {isSearching && students.length === 0 ? (
            <div className="student-search-loading">Buscando...</div>
          ) : students.length === 0 ? (
            <div className="student-search-empty">No se encontraron alumnos</div>
          ) : (
            students.map((student) => (
              <div
                key={student.id}
                className={`student-search-item ${
                  normalizedValue === String(student.id) ? 'selected' : ''
                }`}
                onMouseDown={() => handleSelect(student)}
              >
                <span className="student-search-name">
                  {student.first_name} {student.last_name}
                </span>
                {student.document_number && (
                  <span className="student-search-doc">
                    DNI: {student.document_number}
                  </span>
                )}
              </div>
            ))
          )}
          {!isSearching && debouncedSearch && students.length >= SEARCH_LIMIT && (
            <div className="student-search-footer">
              Mostrando los primeros {SEARCH_LIMIT} resultados. Refina la búsqueda.
            </div>
          )}
        </div>
      )}
      {hiddenInputProps && <input type="hidden" {...hiddenInputProps} />}
    </div>
  );
};

export default StudentSearchSelect;
